/**
 * Dashboard Widgets Slice
 * Redux Toolkit slice for dashboard widget data state management
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { DashboardWidgetsData, WidgetDateRange } from '@creatoros/shared';
import { dashboardService } from '../../services/dashboard.service';
import { logger } from '../../utils/logger';

/**
 * Dashboard Widgets State Interface
 */
export interface DashboardWidgetsState {
  widgetData: DashboardWidgetsData | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: string | null;
  dateRange: WidgetDateRange;
}

/**
 * Initial state
 */
const initialState: DashboardWidgetsState = {
  widgetData: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  dateRange: '30d',
};

/**
 * Fetch dashboard widgets async thunk
 */
export const fetchWidgets = createAsyncThunk<
  DashboardWidgetsData,
  { dateRange?: WidgetDateRange; widgets?: string[] } | undefined,
  { rejectValue: string }
>('dashboardWidgets/fetchWidgets', async (params, { rejectWithValue }) => {
  try {
    logger.debug('Fetching dashboard widgets', {
      event: 'dashboard.widgets.fetch.started',
    });
    const data = await dashboardService.getWidgets(params?.dateRange, params?.widgets);
    logger.debug('Fetched dashboard widgets', {
      event: 'dashboard.widgets.fetch.succeeded',
    });
    return data;
  } catch (error: any) {
    const message = error.message || 'Failed to fetch dashboard widgets';
    logger.error('Failed to fetch dashboard widgets', error, {
      event: 'dashboard.widgets.fetch.failed',
    });
    return rejectWithValue(message);
  }
});

/**
 * Dashboard Widgets Slice
 */
const dashboardWidgetsSlice = createSlice({
  name: 'dashboardWidgets',
  initialState,
  reducers: {
    setDateRange: (state, action: PayloadAction<WidgetDateRange>) => {
      state.dateRange = action.payload;
    },
    clearWidgetData: (state) => {
      state.widgetData = null;
      state.lastFetchedAt = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWidgets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWidgets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.widgetData = action.payload;
        state.lastFetchedAt = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchWidgets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch widgets';
      });
  },
});

/**
 * Selectors
 */
export const selectWidgetData = (state: RootState) => state.dashboardWidgets.widgetData;
export const selectWidgetStats = (state: RootState) => state.dashboardWidgets.widgetData?.stats;
export const selectWidgetCharts = (state: RootState) => state.dashboardWidgets.widgetData?.charts;
export const selectWidgetLists = (state: RootState) => state.dashboardWidgets.widgetData?.lists;
export const selectWidgetsIsLoading = (state: RootState) => state.dashboardWidgets.isLoading;
export const selectWidgetsError = (state: RootState) => state.dashboardWidgets.error;
export const selectWidgetsDateRange = (state: RootState) => state.dashboardWidgets.dateRange;
export const selectWidgetsLastFetchedAt = (state: RootState) =>
  state.dashboardWidgets.lastFetchedAt;

/**
 * Export actions and reducer
 */
export const { setDateRange, clearWidgetData, clearError } = dashboardWidgetsSlice.actions;

export default dashboardWidgetsSlice.reducer;
